export type Interactive = JSONConfigInteractive | LegacyJavascriptInteractive | ElmInteractive;

export type InteractiveTypeName =
  | JSONConfigInteractiveTypeName
  | LegacyJavascriptInteractiveTypeName
  | ElmInteractiveTypeName;

interface BaseInteractive {
  alias: string;
  type: InteractiveTypeName;
}

// TODO rename to SolvableMode
export type SolvableType = 'auto-check' | 'submittable';

// TODO rename to InteractiveMode
export type InteractiveType = 'playground' | SolvableType;

export interface ElmInitializationInstructions {
  modes: { [key: string]: ElmMode };
}

export interface ElmMode {
  module: ElmModule;
  validInteractiveTypes: InteractiveType[];
  usesDandy?: boolean;
  afterInitializing?: (app: ElmApp) => () => void;
}

export interface ElmModule {
  init: (config: { flags: any; node: HTMLElement }) => ElmApp;
}

export interface ElmApp {
  root: HTMLElement;
  ports: {
    [key: string]: {
      subscribe: (handler: (message: any) => void) => void;
      unsubscribe: (handler: (message: any) => void) => void;
      send: (message: any) => void;
    };
  };
}

export interface PortListenersSwitches {
  addListeners: () => void;
  removeListeners: () => void;
}

export type PortSetupFunction = (options: {
  interactiveApp: ElmApp;
  container: HTMLElement;
}) => Promise<PortListenersSwitches>;

/* JSON CONFIG TYPES */
export const JSON_CONFIG_TYPES = {
  parson: 'Parson',
  gridfill: 'Grid Fill',
  toggletags: 'Toggle Tags',
  neuralnetworks: 'Neural Networks',
  orderlogic: 'Order Logic',
  calcdoku: 'Calcdoku',
  hotcold: 'Hot Cold',
  hexapawn: 'Hexapawn',
  codex: 'Codex',
  numberbunchers: 'Number Bunchers',
  dronegame: 'Drone Game',
  sherlocksearch: 'Sherlock Search',
};

export type JSONConfigInteractiveTypeName = keyof typeof JSON_CONFIG_TYPES;

export interface JSONConfigInteractive extends BaseInteractive {
  type: JSONConfigInteractiveTypeName;
  json: string;
}

export const isJSONConfigInteractive = (
  interactive: Interactive,
): interactive is JSONConfigInteractive => {
  return interactive.type in Object.keys(JSON_CONFIG_TYPES);
};

/* LEGACY JAVASCRIPT TYPES */

export const LEGACY_JAVASCRIPT_TYPES = {
  coinflip: 'Coin Flip',
  escher: '3D Graph (Escher)',
  graph2d: '2D Graph',
  huygensclock: "Huygen's Clock",
  operatorsearch: 'Operator Search',
  quantumcircuit: 'Quantum Circuit',
};

export const isLegacyJavascriptInteractive = (
  interactive: Interactive,
): interactive is LegacyJavascriptInteractive => {
  return interactive.type in Object.keys(LEGACY_JAVASCRIPT_TYPES);
};

export type LegacyJavascriptInteractiveTypeName = keyof typeof LEGACY_JAVASCRIPT_TYPES;

export interface LegacyJavascriptInteractive extends BaseInteractive {
  type: LegacyJavascriptInteractiveTypeName;
  json: string;
}

/* Elm Interactives
 *
 * What's the difference between just "elm" and "elm" config, you might wonder?
 *
 * This has to do with the specifics of the mechanism by which we embed the underlying interactive
 *
 * Simsons & Diagrammars, for example, both use elm for their configuration,
 * but ultimately, they exist as their own types
 *
 * ElmConfigs, however, at their bottom are a union type that looks like:
 *
 * type ElmConfigInitializer
 *   = CryptoOneOffs CryptoOneOffs.Configuration
 *   | FillInInteractive FillInInteractive.Configuration
 *   ...
 *
 * And in order to instantiate them, the content author builds this type directly
 * eg.
 *
 *   config =
 *      myConfig
 *         |> FillInInteractive
 *
 *
 * As an additional note, to clarify some confusion, JSONConfig interactive types are
 * also written in elm, but they work differently, in that the JSON configution
 * is interpreted by the elm at runtime, so they behave differently.
 *
 */
export const DIAGRAMMAR_TYPE_NAME = 'diagrammar';
export const SIMSON_TYPE_NAME = 'simson';
export const GENERIC_ELM_ASSET_TYPE_NAME = 'genericElmAsset';
export const ELM_CONFIG_TYPE_NAME = 'elmconfig';

export const ELM_TYPES = {
  [DIAGRAMMAR_TYPE_NAME]: 'Diagrammar',
  [SIMSON_TYPE_NAME]: 'Simson',
  [GENERIC_ELM_ASSET_TYPE_NAME]: 'Generic Elm Asset',
  [ELM_CONFIG_TYPE_NAME]: 'Elmconfig',
};

export type ElmInteractiveTypeName = keyof typeof ELM_TYPES;

export interface ElmInteractive extends BaseInteractive {
  type: ElmInteractiveTypeName;
  elm: string;
}

export interface DiagrammarInteractive extends ElmInteractive {
  type: typeof DIAGRAMMAR_TYPE_NAME;
}

export const isDiagrammarInteractive = (
  interactive: Interactive,
): interactive is DiagrammarInteractive => {
  return interactive.type === DIAGRAMMAR_TYPE_NAME;
};

export interface SimsonInteractive extends ElmInteractive {
  type: typeof SIMSON_TYPE_NAME;
}

export const isSimsonInteractive = (interactive: Interactive): interactive is SimsonInteractive => {
  return interactive.type === SIMSON_TYPE_NAME;
};

export interface ElmConfigInteractive extends ElmInteractive {
  type: typeof ELM_CONFIG_TYPE_NAME;
}

export const isElmConfigInteractive = (
  interactive: Interactive,
): interactive is ElmConfigInteractive => {
  return interactive.type === ELM_CONFIG_TYPE_NAME;
};

export const ALL_INTERACTIVE_TYPES = {
  ...JSON_CONFIG_TYPES,
  ...LEGACY_JAVASCRIPT_TYPES,
  ...ELM_TYPES,
};
